import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ReceiptLong,
  TableRestaurant,
  CheckCircle,
  DoneAll,
  HourglassBottom,
} from "@mui/icons-material";
import { selectOrders, selectTables, ITEM_STATUS } from "../Store/Slices/restaurantSlice";
import SettleBillDialog from "../Components/Restaurant/SettleBillDialog";
import styles from "./BillingPage.module.css";

const BillingPage = () => {
  const orders = useSelector(selectOrders);
  const tables = useSelector(selectTables);
  const currency = useSelector((state) => state.currency);
  const symbol = currency?.currencySymbol || "";
  const [selectedBillTableId, setSelectedBillTableId] = useState(null);

  const tableInfo = (tableId) => tables.find((t) => t.id === tableId);

  const bills = useMemo(() => {
    return Object.values(orders)
      .filter((order) => order.items.length > 0)
      .map((order) => {
        const subtotal = order.items.reduce(
          (sum, line) =>
            sum + line.qty * line.price * (1 - (line.discount || 0) / 100),
          0
        );
        const tax = subtotal * 0.05;
        const qty = order.items.reduce((sum, line) => sum + line.qty, 0);
        const allServed = order.items.every(
          (line) => line.status === ITEM_STATUS.SERVED
        );
        const pending = order.items.filter(
          (line) => line.status !== ITEM_STATUS.SERVED
        ).length;
        return {
          tableId: order.tableId,
          invoiceNo: order.invoiceNo,
          items: order.items,
          subtotal,
          tax,
          total: subtotal + tax,
          qty,
          allServed,
          pending,
        };
      })
      .sort((a, b) => String(a.tableId).localeCompare(String(b.tableId)));
  }, [orders]);

  const grandTotal = bills.reduce((sum, bill) => sum + bill.total, 0);
  const selectedBill = bills.find((bill) => bill.tableId === selectedBillTableId);
  const selectedTable = selectedBill ? tableInfo(selectedBill.tableId) : null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <ReceiptLong /> Billing &amp; Settlement
          </h1>
          <p className={styles.subtitle}>
            {bills.length} open bill{bills.length === 1 ? "" : "s"} · Outstanding{" "}
            {symbol} {grandTotal.toFixed(2)}
          </p>
        </div>
      </div>

      {bills.length === 0 ? (
        <div className={styles.emptyState}>
          <DoneAll className={styles.emptyIcon} />
          <p>No open bills. Every table is settled.</p>
        </div>
      ) : (
        <div className={styles.billGrid}>
          {bills.map((bill) => {
            const table = tableInfo(bill.tableId);
            return (
              <div key={bill.tableId} className={styles.billCard}>
                <div className={styles.billHead}>
                  <span className={styles.billTable}>
                    <TableRestaurant fontSize="small" /> Table {table?.name}
                  </span>
                  <span className={styles.billInvoice}>{bill.invoiceNo}</span>
                </div>

                <div className={styles.billBody}>
                  {bill.items.map((line) => (
                    <div key={line.lineId} className={styles.billLine}>
                      <span className={styles.billQty}>{line.qty}×</span>
                      <span className={styles.billName}>{line.title}</span>
                      <span className={styles.billAmount}>
                        {symbol}{" "}
                        {(
                          line.qty *
                          line.price *
                          (1 - (line.discount || 0) / 100)
                        ).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className={styles.billTotals}>
                  <div className={styles.totalRow}>
                    <span>Subtotal ({bill.qty} items)</span>
                    <span>
                      {symbol} {bill.subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className={styles.totalRow}>
                    <span>Tax (5%)</span>
                    <span>
                      {symbol} {bill.tax.toFixed(2)}
                    </span>
                  </div>
                  <div className={styles.grandRow}>
                    <span>Total</span>
                    <span>
                      {symbol} {bill.total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className={styles.billStatus}>
                  {bill.allServed ? (
                    <span className={styles.statusServed}>
                      <CheckCircle fontSize="inherit" /> All served
                    </span>
                  ) : (
                    <span className={styles.statusPending}>
                      <HourglassBottom fontSize="inherit" /> {bill.pending} item
                      {bill.pending === 1 ? "" : "s"} pending
                    </span>
                  )}
                </div>

                <button
                  className={styles.settleBtn}
                  onClick={() => setSelectedBillTableId(bill.tableId)}
                >
                  <ReceiptLong fontSize="small" /> Settle Bill
                </button>
              </div>
            );
          })}
        </div>
      )}

      {selectedBill && selectedTable && (
        <SettleBillDialog
          open={Boolean(selectedBillTableId)}
          onClose={() => setSelectedBillTableId(null)}
          table={selectedTable}
          order={selectedBill}
        />
      )}
    </div>
  );
};

export default BillingPage;
